import React, { useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';

interface ICreateTeamModal {
  setNewMode: any;
  newTeamName: string;
  setNewTeamName: any;
  handleCreateNew: any;
  createTeamInviteEmails?: string[];
  currentCreateTeamInviteEmail?: string;
  setCurrentCreateTeamInviteEmail?: any;
  handleAddCreateTeamInviteEmail?: any;
  handleRemoveCreateTeamInviteEmail?: any;
  handleCreateTeamInviteKeyDown?: any;
}

const CreateTeamModal: React.FC<ICreateTeamModal> = ({
  setNewMode,
  setNewTeamName,
  newTeamName,
  handleCreateNew,
  createTeamInviteEmails = [],
  currentCreateTeamInviteEmail = '',
  setCurrentCreateTeamInviteEmail = () => null,
  handleAddCreateTeamInviteEmail = () => null,
  handleRemoveCreateTeamInviteEmail = () => null,
  handleCreateTeamInviteKeyDown = () => null,
}) => {
  const inviteEmailInputRef = useRef<HTMLInputElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await handleCreateNew(newTeamName);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='create-team-modal'>
      <div className='create-team-header'>
        <div className='create-team-header-content'>
          <div className='create-team-header-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M12 5V19M5 12H19'
                stroke='#4895EF'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='create-team-header-group'>
            <div className='create-team-header-primary'>Create New Team</div>
            <div className='create-team-header-secondary'>
              You will automatically be made the admin of this new team.
            </div>
          </div>
        </div>
        <div
          className='create-team-header-close'
          onClick={() => {
            if (!isSubmitting) {
              setNewMode(false);
            }
          }}
        >
          <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path d='M18 6L6 18M6 6L18 18' stroke='var(--card-text)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
          </svg>
        </div>
      </div>
      <div className='create-team-body'>
        <div className='create-team-body-frame'>
          <div className='create-team-label'>New Team Name</div>
          <div className='team-name-form'>
            <input
              id='searchVal'
              placeholder='Enter your team name...'
              type='text'
              className='create-team-name-input'
              name='newTeamName'
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
            />
          </div>
          <div className='create-team-label'>Team Members</div>
          <div
            className='team-members-chip-input invite-user-modal-chip-input'
            onClick={() => inviteEmailInputRef.current?.focus()}
          >
            <div className='team-members-chip-list'>
              {createTeamInviteEmails.map((email: string, index: number) => (
                <div key={`create-team-email-${index}`} className='team-member-chip'>
                  <div className='team-member-chip-text'>{email}</div>
                  <button
                    type='button'
                    className='team-member-remove-btn'
                    onClick={() => handleRemoveCreateTeamInviteEmail(index)}
                    aria-label={`Remove team invite email ${index + 1}`}
                  >
                    x
                  </button>
                </div>
              ))}
              <input
                ref={inviteEmailInputRef}
                id='create-team-email'
                placeholder={
                  createTeamInviteEmails.length === 0 ? 'email@example.com' : ''
                }
                type='text'
                className='team-members-chip-input-field'
                name='createTeamInviteEmail'
                value={currentCreateTeamInviteEmail}
                onChange={(e) =>
                  setCurrentCreateTeamInviteEmail(e.target.value)
                }
                onKeyDown={handleCreateTeamInviteKeyDown}
              />
            </div>
          </div>
          <div
            className='team-members-add-btn'
            onClick={handleAddCreateTeamInviteEmail}
          >
            + Add another member
          </div>
          <div className='team-members-helper-text'>
            Press Enter or click add to append multiple emails into the same field.
          </div>
        </div>
      </div>
      <div className='create-team-modal-footer'>
        <div className='create-team-modal-footer-wrap'></div>
        <div className='create-team-modal-footer-actions'>
          <div className='create-team-modal-footer-actions-frame'>
            <div
              className='create-team-footer-cancel'
              onClick={() => {
                if (!isSubmitting) {
                  setNewMode(false);
                }
              }}
              style={{
                opacity: isSubmitting ? 0.6 : 1,
                pointerEvents: isSubmitting ? 'none' : 'auto',
              }}
            >
              <div className='create-team-footer-cancel-text'>Cancel</div>
            </div>
            <div
              className='create-team-footer-create'
              onClick={handleSubmit}
              style={{
                opacity: isSubmitting ? 0.85 : 1,
                pointerEvents: isSubmitting ? 'none' : 'auto',
              }}
            >
              <div
                className='create-team-footer-create-text'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {isSubmitting && (
                  <CircularProgress size={16} style={{ color: '#fff' }} />
                )}
                {isSubmitting ? 'Creating...' : 'Create Team'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTeamModal;
